import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { AuthContext } from '../../App';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid credentials');
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F4]">
        <div className="animate-spin w-8 h-8 border-2 border-[#C4A882] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F4F4] px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="bg-white p-8 shadow-sm">
          {/* Logo */}
          <div className="text-center mb-8">
            <span className="font-heading text-2xl font-semibold tracking-tight text-[#C4A882]">in-dekor™</span>
            <div className="w-12 h-12 mx-auto mb-4 mt-6 bg-[#1A1A1A] flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-[#1A1A1A]">Admin Login</h1>
            <p className="font-body text-sm text-[#666666] mt-2">Sign in to access the admin panel</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 text-sm" data-testid="login-error">
                {error}
              </div>
            )}
            
            <div>
              <Label htmlFor="email" className="font-body text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 rounded-none"
                placeholder="admin@indekor.com"
                required
                data-testid="admin-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="font-body text-sm">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 rounded-none"
                placeholder="••••••••"
                required
                data-testid="admin-password-input"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="font-body text-xs text-[#666666] text-center mt-6">
            Protected area. Authorized personnel only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
